import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { languageOptions } from '@app/constants';
import { paths } from '@app/constants/paths.constant';
import { changeLanguage, getCurrentAppTheme } from '@app/utils';
import { useUnsubscribeByToken } from '@data/hooks';
import {
  Button,
  Card,
  AntdConfigProvider,
  AntdGrid,
  AntdSelect,
  AntdSpace,
  AntdTypography,
  Spinner,
  useTheme,
  isYWC,
  YWCLogoSmall,
} from '@ui';

const { useBreakpoint } = AntdGrid;
const { Text, Title } = AntdTypography;

const UnsubscribePage = () => {
  const { sm } = useBreakpoint();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { bgColor, baseSelect, textColorDark } = useTheme();

  const [token] = useQueryParam('token', StringParam);

  const { mutate: unsubscribe, isPending, isSuccess, isError } = useUnsubscribeByToken();
  const { PublicLogo, publicLogoProps, AdditionalLogo, additionalLogoProps } = getCurrentAppTheme();

  const hasFailed = isError || !token;
  // Only unsubscribe on an explicit click — never on page load. Email security scanners
  // (SafeLinks, Mimecast, …) and some clients auto-fetch links, which would otherwise
  // unsubscribe people who never clicked.
  const showConfirm = !!token && !isPending && !isSuccess && !isError;

  const handleUnsubscribe = () => {
    if (token) unsubscribe({ token });
  };

  const getTitle = () => {
    if (isSuccess) return t('unsubscribe_page.success_title');
    if (hasFailed) return t('unsubscribe_page.error_title');
    if (isPending) return t('unsubscribe_page.loading_title');
    return t('unsubscribe_page.confirm_title');
  };

  return (
    <>
      <div
        className="hidden sm:block pl-6 sm:pl-0 pt-16 pb-11 sm:pt-0 rounded-bl-3xl rounded-br-3xl"
        style={{ backgroundColor: sm ? 'transparent' : bgColor }}
      >
        <Title className="text-base-100 mb-0 mt-0 sm:mt-auto">{getTitle()}</Title>
        {isYWC && <PublicLogo {...publicLogoProps} />}
      </div>
      <AntdSpace />
      <Card className="border-none sm:border-solid sm:max-w-md">
        <div className="flex w-full self-start justify-between mb-4">
          {isYWC ? (
            <YWCLogoSmall className="h-20 w-auto md:hidden" />
          ) : (
            <PublicLogo {...publicLogoProps} />
          )}
          <div className="flex items-center gap-2 ml-auto">
            <a
              href={t('faq.link')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 md:hidden"
              style={{ color: textColorDark }}
            >
              {t('faq.text_short')}
              <QuestionCircleOutlined />
            </a>
            <AntdConfigProvider theme={{ components: { Select: baseSelect } }}>
              <AntdSelect
                variant="borderless"
                defaultValue={i18n.language}
                onChange={(value) => changeLanguage(value, i18n)}
                aria-label="Language"
                options={languageOptions()}
              />
            </AntdConfigProvider>
          </div>
        </div>

        <div className="sm:hidden">
          <Title className="text-base-100 mb-4" style={{ color: textColorDark }}>
            {getTitle()}
          </Title>
        </div>

        {isPending && <Spinner />}

        {showConfirm && (
          <>
            <Text className="block mb-6">{t('unsubscribe_page.confirm_message')}</Text>
            <Button
              className="w-full"
              type="primary"
              data-testid="confirm-unsubscribe-button"
              onClick={handleUnsubscribe}
            >
              {t('unsubscribe_page.confirm_button')}
            </Button>
            <Button
              className="w-full mt-3"
              type="link"
              data-testid="manage-preferences-link"
              onClick={() =>
                navigate(`/${paths.app}/${paths.profile.root}/${paths.profile.notifications}`)
              }
            >
              {t('unsubscribe_page.manage_preferences')}
            </Button>
          </>
        )}

        {isSuccess && (
          <>
            <Text className="block mb-6">{t('unsubscribe_page.success_message')}</Text>
            <Button
              className="w-full"
              type="primary"
              data-testid="manage-preferences-button"
              onClick={() =>
                navigate(`/${paths.app}/${paths.profile.root}/${paths.profile.notifications}`)
              }
            >
              {t('unsubscribe_page.manage_preferences')}
            </Button>
          </>
        )}

        {hasFailed && !isPending && (
          <>
            <Text className="block mb-6">{t('unsubscribe_page.error_message')}</Text>
            <Button
              className="w-full"
              type="primary"
              data-testid="back-to-login-button"
              onClick={() => navigate(`/${paths.sign_in}`)}
            >
              {t('unsubscribe_page.back_to_login')}
            </Button>
          </>
        )}

        <AdditionalLogo {...additionalLogoProps} />
      </Card>
    </>
  );
};

export default UnsubscribePage;
